import axios from 'axios';

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

// Simple obfuscation for localStorage tokens (not encryption, but prevents casual reading)
const TOKEN_PREFIX = 'po_';
function encodeToken(token) { return TOKEN_PREFIX + btoa(token); }
function decodeToken(stored) {
  if (!stored || !stored.startsWith(TOKEN_PREFIX)) return stored;
  try { return atob(stored.slice(TOKEN_PREFIX.length)); } catch { return null; }
}

function getToken(key) { return decodeToken(localStorage.getItem(key)); }
function setToken(key, value) { localStorage.setItem(key, encodeToken(value)); }
function removeToken(key) { localStorage.removeItem(key); }

// Request interceptor — attach token
api.interceptors.request.use((config) => {
  const token = getToken('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor — refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401 && !error.config._retry && !error.config.url?.includes('/auth/')) {
      error.config._retry = true;
      try {
        const refreshToken = getToken('refresh_token');
        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/refresh`,
          {},
          {
            withCredentials: true,
            headers: refreshToken ? { Authorization: `Bearer ${refreshToken}` } : {},
          }
        );
        if (res.data?.access_token) {
          setToken('access_token', res.data.access_token);
        }
        return api(error.config);
      } catch {
        removeToken('access_token');
        removeToken('refresh_token');
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export default api;

export function storeTokens(data) {
  if (data?.access_token) setToken('access_token', data.access_token);
  if (data?.refresh_token) setToken('refresh_token', data.refresh_token);
}

export function clearTokens() {
  removeToken('access_token');
  removeToken('refresh_token');
}

export function hasStoredToken() {
  return !!getToken('access_token');
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}
